import { useCallback, useEffect, useMemo, useState } from "react";

import {
  anchorHit,
  findHits,
  marksByEntry,
  stepHit,
  type Hit,
  type RowMarks,
  type Searchable,
} from "./logFind";

export interface LogFind {
  open: boolean;
  /** Bumped on every `show`, so a second Ctrl+F puts the caret back in an already-open field. */
  opened: number;
  query: string;
  hits: readonly Hit[];
  /** Index into `hits` of the one you are on; null when there is none. */
  current: number | null;
  marks: ReadonlyMap<string, RowMarks>;
  /** Opens with the last query, which the band selects so typing replaces it. */
  show: () => void;
  close: () => void;
  setQuery: (query: string) => void;
  step: (dir: 1 | -1) => void;
}

/**
 * The find band's state (日本-603). Churn, not a place: none of it is in `NavState`, so Back does
 * not close it and a reload does not bring it back (日本-321's rule for a segment toggle).
 *
 * `onJump` is how a hit becomes the focused row and scrolls into view — Read owns both, through the
 * same path `↑`/`↓` take. `topRow` answers "where does the reader's eye start", which is where a
 * fresh query searches from.
 */
export function useLogFind({
  entries,
  logId,
  onJump,
  topRow,
}: {
  entries: readonly Searchable[];
  logId: string | null;
  onJump: (hit: Hit) => void;
  topRow: () => number;
}): LogFind {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(0);
  const [query, setQueryState] = useState("");
  /**
   * The hit you are on, held by IDENTITY rather than by index: a translation arriving for an
   * earlier row, or an earlier sentence edited or deleted, shifts every index after it, and an
   * index would silently put the ring on a different sentence.
   */
  const [current, setCurrent] = useState<Hit | null>(null);

  const hits = useMemo(() => (open ? findHits(entries, query) : []), [open, entries, query]);

  const safeCurrent = useMemo(() => {
    if (current === null) return null;
    const at = hits.findIndex(
      (hit) => hit.entryId === current.entryId && hit.field === current.field && hit.start === current.start,
    );
    return at === -1 ? null : at;
  }, [current, hits]);
  const marks = useMemo(() => marksByEntry(hits, safeCurrent), [hits, safeCurrent]);

  /**
   * **Switching logs closes the band.** Its count and its current hit are about one log; carrying
   * them into another would show `3 / 17件` over a log with none of those seventeen.
   */
  useEffect(() => {
    setOpen(false);
    setCurrent(null);
  }, [logId]);

  const show = useCallback(() => {
    setOpen(true);
    setOpened((n) => n + 1);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setCurrent(null);
  }, []);

  const setQuery = useCallback(
    (next: string) => {
      setQueryState(next);
      const found = findHits(entries, next);
      // Narrowing `共` to `共に` stays where you are rather than going back to the top of the
      // viewport; a fresh query starts from where your eye is.
      const from = safeCurrent !== null && hits[safeCurrent] ? hits[safeCurrent].row : topRow();
      const at = anchorHit(found, from);
      // **No hit leaves the log where it was**: a typo must not throw the reader back to the top.
      const hit = at === null ? undefined : found[at];
      setCurrent(hit ?? null);
      if (hit) onJump(hit);
    },
    [entries, hits, safeCurrent, onJump, topRow],
  );

  const step = useCallback(
    (dir: 1 | -1) => {
      // With no current hit — the band reopened on its last query — the first step starts from
      // where the reader's eye is, as typing does, not from the top of a 2,000-row log.
      let at: number | null;
      if (safeCurrent === null && hits.length > 0) {
        const from = topRow();
        const below = anchorHit(hits, from);
        if (dir === 1) at = below;
        else {
          const firstAtOrBelow = hits.findIndex((hit) => hit.row >= from);
          at = firstAtOrBelow <= 0 ? hits.length - 1 : firstAtOrBelow - 1;
        }
      } else {
        at = stepHit(safeCurrent, hits.length, dir);
      }
      const hit = at === null ? undefined : hits[at];
      setCurrent(hit ?? null);
      if (hit) onJump(hit);
    },
    [safeCurrent, hits, onJump, topRow],
  );

  return { open, opened, query, hits, current: safeCurrent, marks, show, close, setQuery, step };
}
